import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './TiendaPage.css';
import Header from './Header';
import AnimatedBackground from './AnimatedBackground';
import StoreCarousel from './StoreCarousel';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';
import { STORE_CATEGORY_SLUGS } from '../constants/storeCategories';
import { groupProductsForStore } from '../utils/productPresentations';
import { searchTextIncludes } from '../utils/searchText';
import { TiendaServicesCrossLink } from './EcosystemCrossLink';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/** Igual que en MySQL tras unify_product_categories.sql: minúsculas y guiones. */
const normalizeCategorySlug = (raw) =>
  (raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');

const VALID_CATEGORIES = [...STORE_CATEGORY_SLUGS, 'todos'];

/** Etiquetas en UI (breadcrumb / título); los slugs siguen siendo los de URL/BD. */
const CATEGORY_UI_LABELS = {
  todos: 'Todos los productos',
  ropa: 'Ropa',
  'ropa-hombre': 'Ropa hombre',
  'ropa-mujer': 'Ropa mujer',
  'productos-naturales': 'Productos naturales',
  combos: 'Combos',
  'instrumentos-musicales': 'Instrumentos musicales',
  'joyeria-artesanias': 'Joyería y Artesanías',
  bienestar: 'Bienestar',
  tecnologia: 'Tecnología',
  jardineria: 'Jardinería',
  sale: 'Ofertas (descuentos)'
};

const SUBCATEGORY_UI_LABELS = {
  'frutos-secos':            { label: 'Frutos Secos',              icon: 'fas fa-circle-notch' },
  'semillas-saludables':     { label: 'Semillas Saludables',       icon: 'fas fa-seedling' },
  'superalimentos-vitalidad':{ label: 'Superalimentos y Vitalidad',icon: 'fas fa-bolt' },
  'grasas-saludables':       { label: 'Aceites y Grasas',          icon: 'fas fa-tint' },
  'especias':                { label: 'Especias y Sabores',        icon: 'fas fa-pepper-hot' },
  'deshidratados':           { label: 'Deshidratados',             icon: 'fas fa-sun' },
  'hora-del-te':             { label: 'Hora del Té',               icon: 'fas fa-mug-hot' },
  'miel-derivados':          { label: 'Miel y Derivados',          icon: 'fas fa-star' },
  'cuidado-aseo':            { label: 'Cuidado Personal',          icon: 'fas fa-spa' },
  'snacks-saludables':       { label: 'Snacks Saludables',         icon: 'fas fa-cookie-bite' },
  'cereales-harinas':        { label: 'Cereales y Harinas',        icon: 'fas fa-bread-slice' },
  'sales-endulzantes':       { label: 'Sales y Endulzantes',       icon: 'fas fa-cubes' },
};

const SUBCATEGORY_ORDER = [
  'frutos-secos','semillas-saludables','superalimentos-vitalidad','grasas-saludables',
  'especias','deshidratados','hora-del-te','miel-derivados',
  'cuidado-aseo','snacks-saludables','cereales-harinas','sales-endulzantes'
];

/** Categorías principales que muestran barra de subfiltros (frutos secos, etc.) */
const SUBFILTER_PARENT_CATEGORIES = ['todos', 'combos', 'productos-naturales'];

const getCategoryFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const cat = normalizeCategorySlug(params.get('category'));
  if (VALID_CATEGORIES.includes(cat)) return cat;
  return 'todos';
};

const getSubcategoryFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('subcategory') || null;
};

/** Separa ítems de «Incluye: A, B y C» sin partir nombres con «y» (ej. Avena y Miel). */
const splitItemList = (itemsStr) => {
  const s = String(itemsStr || '').trim();
  if (!s) return [];
  const normalized = s.replace(/\s+y\s+(?=[^,]+$)/i, ', ');
  return normalized
    .split(/\s*,\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
};

const countListedItems = (itemsStr) => splitItemList(itemsStr).length;

/** Productos mix en tienda: nombre contiene «mix» o fruta deshidratada mix. */
const isMixDisplayProduct = (product) => {
  const name = String(product?.name || '').toLowerCase();
  return /\bmix\b/.test(name) || name.includes('fruta deshidratada mix');
};

/** Una línea para tarjeta combo/mix: extrae «Incluye:» o «Contiene:» de la descripción de BD. */
const productKitTagline = (description) => {
  const text = String(description || '').trim();
  if (!text) return '';
  const listMatch = text.match(/(?:Incluye|Contiene):\s*([^.]+)/i);
  if (listMatch && listMatch[1]) {
    const items = listMatch[1].trim();
    const count = countListedItems(items);
    const label = count === 1 ? '1 producto' : `${count} productos`;
    return count > 0 ? `${label}: ${items}` : items;
  }
  const first = text.split(/[.!?]/)[0]?.trim();
  return first && first.length <= 120 ? first : `${first?.slice(0, 117)}…`;
};

const TiendaPage = () => {
  const [selectedCategory, setSelectedCategory] = useState(getCategoryFromUrl);
  const [selectedSubcategory, setSelectedSubcategory] = useState(getSubcategoryFromUrl);
  const [apiProducts, setApiProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('latest');
  const [searchQuery, setSearchQuery] = useState('');

  const { addToCart, openCart } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Si cambia querystring en /tienda, mantener el filtro sincronizado sin recargar.
  useEffect(() => {
    const syncFromUrl = () => {
      setSelectedCategory(getCategoryFromUrl());
      setSelectedSubcategory(getSubcategoryFromUrl());
    };
    window.addEventListener('popstate', syncFromUrl);
    return () => window.removeEventListener('popstate', syncFromUrl);
  }, []);

  // Sincronizar URL con categoría/subcategoría seleccionada
  useEffect(() => {
    const base = window.location.pathname;
    if (selectedCategory === 'todos') {
      if (selectedSubcategory) {
        window.history.replaceState({}, '', `${base}?subcategory=${selectedSubcategory}`);
      } else {
        window.history.replaceState({}, '', base);
      }
    } else if (selectedSubcategory) {
      window.history.replaceState({}, '', `${base}?category=${selectedCategory}&subcategory=${selectedSubcategory}`);
    } else {
      window.history.replaceState({}, '', `${base}?category=${selectedCategory}`);
    }
  }, [selectedCategory, selectedSubcategory]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/products`, { params: { limit: 100 } });
        const productsData = response.data.data?.products || response.data.products || [];
        setApiProducts(Array.isArray(productsData) ? productsData : []);
        setError(null);
      } catch (err) {
        console.error('Error cargando productos:', err);
        setError('Error al cargar los productos. Por favor, intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const carouselSlides = [
    {
      type: 'image',
      src: `${process.env.PUBLIC_URL || ''}/images/tienda/combos/kit-desayuno-saludable.png`,
      fallbackSrc: `${process.env.PUBLIC_URL || ''}/images/hero-carousel-tienda-proposito.png`,
      subtitle: 'Empieza aquí',
      title: 'Combo Empezar en Bienestar',
      description: 'Tu primer gancho en Javiland: productos naturales curados para probar rutinas distintas a las de siempre. Ideal si llegas por primera vez.',
      cta: {
        text: 'Ver combos',
        onClick: () => { setSelectedCategory('combos'); setSelectedSubcategory(null); }
      }
    },
    {
      type: 'image',
      src: `${process.env.PUBLIC_URL || ''}/images/tienda-carousel-cafe-kaaba.png`,
      fallbackSrc: `${process.env.PUBLIC_URL || ''}/images/hero-carousel-tienda-proposito.png`,
      subtitle: 'Combos semanales',
      title: 'Una semana de bienestar en un kit',
      description: 'Combo Familiar, Jardín, Colegio, Universidad y Ejecutivo — más los kits clásicos. Porciones pensadas para ~7 días.',
      cta: {
        text: 'Explorar combos',
        onClick: () => { setSelectedCategory('combos'); setSelectedSubcategory(null); }
      }
    },
    {
      type: 'image',
      src: `${process.env.PUBLIC_URL || ''}/images/tienda-carousel-coleccion-javiland.png`,
      fallbackSrc: `${process.env.PUBLIC_URL || ''}/images/tienda-carousel-cafe-kaaba.png`,
      subtitle: 'Productos naturales',
      title: 'Superalimentos, frutos secos y más',
      description: 'Seleccionados con criterio. Disponibles en stock y bajo pedido. Envíos a toda Colombia.',
      cta: {
        text: 'Ver productos',
        onClick: () => { setSelectedCategory('productos-naturales'); setSelectedSubcategory(null); }
      }
    },
    {
      type: 'image',
      src: `${process.env.PUBLIC_URL || ''}/images/hero-carousel-tienda-proposito.png`,
      fallbackSrc: `${process.env.PUBLIC_URL || ''}/images/tienda-carousel-cafe-kaaba.png`,
      subtitle: 'Beneficio Javiland',
      title: 'Envío nacional $12.000',
      description: 'Compra con tranquilidad. Productos naturales seleccionados, entregados en todo Colombia.',
      cta: {
        text: 'Explorar tienda',
        onClick: () => { setSelectedCategory('todos'); setSelectedSubcategory(null); }
      }
    }
  ];

  const mapApiToCard = (p) => {
    const price = parseFloat(p.price ?? 0);
    const rawOriginal =
      parseFloat(p.originalPrice ?? p.original_price ?? p.compare_at_price ?? p.compareAtPrice ?? price);
    const originalPrice = Number.isFinite(rawOriginal) && rawOriginal > price ? rawOriginal : price;
    const stock = parseInt(p.stock ?? 0, 10);
    const isDropshipping = p.fulfillment === 'dropshipping';
    const img = (p.images && p.images[0]) || p.image || '/images/placeholders/placeholder-product.png';
    return {
      id: p.id,
      brand: p.brand || 'Javiland',
      name: p.name,
      category: p.category,
      subcategory: p.subcategory || '',
      price,
      originalPrice,
      image: img,
      images: p.images || [img],
      description: p.description || '',
      features: p.features || [],
      // Dropshipping: disponible aunque stock=0 (se pide al proveedor al vender)
      inStock: stock > 0 || isDropshipping,
      isDropshipping,
      stock,
      variants: [{ size: 'Único', color: 'Único', stock, price }],
      isCamisetaAgrupada: false,
      isNew: false
    };
  };

  /** Productos por categoría asignable en BD + vista combinada `ropa`. */
  const filteredProducts = useMemo(() => {
    const activeProducts = (apiProducts || []).filter(
      (p) => p.status !== 'inactive' && p.status !== 'upcoming'
    );

    const mappedForSale = activeProducts.map((p) => mapApiToCard(p));

    if (selectedCategory === 'sale') {
      return mappedForSale.filter((p) => p.originalPrice > p.price);
    }

    const buildListForAssignableSlug = (slug) => {
      const list = [];
      const byCat = activeProducts.filter((p) => normalizeCategorySlug(p.category) === slug);

      if (slug === 'ropa-hombre') {
        const camisetas = (apiProducts || []).filter((p) => p.name?.toLowerCase().includes('camiseta'));
        if (camisetas.length > 0) {
          const primeraCamiseta = camisetas[0];
          const productImage =
            primeraCamiseta.images?.length > 0
              ? primeraCamiseta.images[0]
              : primeraCamiseta.image || '/images/placeholders/placeholder-product.png';
          const totalStock = camisetas.reduce((sum, c) => sum + (c.stock || 0), 0);
          list.push({
            id: primeraCamiseta.id,
            brand: 'Javiland',
            name: 'Camiseta Javiland',
            category: 'ropa-hombre',
            price: parseFloat(primeraCamiseta.price),
            originalPrice: parseFloat(primeraCamiseta.price),
            image: productImage,
            images:
              primeraCamiseta.images || [
                primeraCamiseta.image || '/images/placeholders/placeholder-product.png'
              ],
            description:
              primeraCamiseta.description ||
              'Camiseta 100% algodón orgánico con diseño exclusivo de Javiland.',
            features: primeraCamiseta.features || [],
            inStock: totalStock > 0,
            stock: totalStock,
            variants: [
              {
                size: 'Seleccionar',
                color: 'Único',
                stock: totalStock,
                price: parseFloat(primeraCamiseta.price)
              }
            ],
            isCamisetaAgrupada: true,
            isNew: true
          });
        }
        const otrosRh = byCat.filter((p) => !p.name?.toLowerCase().includes('camiseta'));
        otrosRh.forEach((p) => list.push(mapApiToCard(p)));
      } else {
        byCat.forEach((p) => list.push(mapApiToCard(p)));
      }

      return list;
    };

    if (selectedCategory === 'ropa') {
      return [...buildListForAssignableSlug('ropa-hombre'), ...buildListForAssignableSlug('ropa-mujer')];
    }

    if (selectedCategory === 'todos') {
      const allAssignableSlugs = [
        'combos',
        'ropa-hombre',
        'ropa-mujer',
        'productos-naturales',
        'instrumentos-musicales',
        'joyeria-artesanias',
        'bienestar',
        'tecnologia',
        'jardineria',
      ];
      let list = allAssignableSlugs.flatMap((slug) => buildListForAssignableSlug(slug));
      if (selectedSubcategory) {
        list = list.filter((p) => p.subcategory === selectedSubcategory);
      }
      return list;
    }

    const list = buildListForAssignableSlug(selectedCategory);
    if (selectedSubcategory) {
      return list.filter((p) => p.subcategory === selectedSubcategory);
    }
    return list;
  }, [apiProducts, selectedCategory, selectedSubcategory]);

  const sidebarBlocks = useMemo(() => {
    const countInCategory = (catId) =>
      (apiProducts || []).filter(
        (p) =>
          normalizeCategorySlug(p.category) === catId &&
          p.status !== 'inactive' &&
          p.status !== 'upcoming'
      ).length;

    const countInSubcategory = (subcat) =>
      (apiProducts || []).filter(
        (p) =>
          normalizeCategorySlug(p.category) === 'productos-naturales' &&
          (p.subcategory || '') === subcat &&
          p.status !== 'inactive' &&
          p.status !== 'upcoming'
      ).length;

    const naturalSubcats = SUBCATEGORY_ORDER
      .map((slug) => ({
        id: slug,
        name: SUBCATEGORY_UI_LABELS[slug]?.label || slug,
        icon: SUBCATEGORY_UI_LABELS[slug]?.icon || 'fas fa-circle',
        count: countInSubcategory(slug),
      }))
      .filter((sc) => sc.count > 0);

    const ropaTotal = countInCategory('ropa-hombre') + countInCategory('ropa-mujer');

    const totalCount =
      ropaTotal +
      countInCategory('combos') +
      countInCategory('productos-naturales') +
      countInCategory('instrumentos-musicales') +
      countInCategory('joyeria-artesanias') +
      countInCategory('bienestar') +
      countInCategory('tecnologia') +
      countInCategory('jardineria');

    const allItem = {
      id: 'todos',
      name: 'Todos los productos',
      count: totalCount,
      icon: 'fas fa-th-large',
      nested: false,
    };

    const ropaItems = [
      { id: 'ropa', name: 'Ropa', count: ropaTotal, icon: 'fas fa-tshirt', nested: false },
      {
        id: 'ropa-hombre',
        name: 'Ropa hombre',
        count: countInCategory('ropa-hombre'),
        icon: 'fas fa-mars',
        nested: true
      },
      {
        id: 'ropa-mujer',
        name: 'Ropa mujer',
        count: countInCategory('ropa-mujer'),
        icon: 'fas fa-venus',
        nested: true
      }
    ];

    const otherItems = [
      {
        id: 'combos',
        name: 'Combos',
        count: countInCategory('combos'),
        icon: 'fas fa-box-open',
        nested: false
      },
      {
        id: 'productos-naturales',
        name: 'Productos naturales',
        count: countInCategory('productos-naturales'),
        icon: 'fas fa-leaf',
        nested: false
      },
      {
        id: 'instrumentos-musicales',
        name: 'Instrumentos musicales',
        count: countInCategory('instrumentos-musicales'),
        icon: 'fas fa-music',
        nested: false
      },
      {
        id: 'joyeria-artesanias',
        name: 'Joyería y Artesanías',
        count: countInCategory('joyeria-artesanias'),
        icon: 'fas fa-gem',
        nested: false
      },
      {
        id: 'bienestar',
        name: 'Bienestar',
        count: countInCategory('bienestar'),
        icon: 'fas fa-spa',
        nested: false
      },
      {
        id: 'tecnologia',
        name: 'Tecnología',
        count: countInCategory('tecnologia'),
        icon: 'fas fa-laptop',
        nested: false
      },
      {
        id: 'jardineria',
        name: 'Jardinería',
        count: countInCategory('jardineria'),
        icon: 'fas fa-seedling',
        nested: false
      }
    ];

    const saleCount = (apiProducts || [])
      .filter((p) => p.status !== 'inactive' && p.status !== 'upcoming')
      .map((p) => mapApiToCard(p))
      .filter((p) => p.originalPrice > p.price).length;

    return { allItem, ropaItems, otherItems, saleCount, naturalSubcats };
  }, [apiProducts]);

  const visibleSubcats = useMemo(() => {
    if (!SUBFILTER_PARENT_CATEGORIES.includes(selectedCategory)) return [];

    const countInScope = (subcat) =>
      (apiProducts || []).filter((p) => {
        if (p.status === 'inactive' || p.status === 'upcoming') return false;
        if ((p.subcategory || '') !== subcat) return false;
        if (selectedCategory === 'todos') return true;
        return normalizeCategorySlug(p.category) === selectedCategory;
      }).length;

    return SUBCATEGORY_ORDER.map((slug) => ({
      id: slug,
      name: SUBCATEGORY_UI_LABELS[slug]?.label || slug,
      count: countInScope(slug),
    })).filter((sc) => sc.count > 0);
  }, [apiProducts, selectedCategory]);

  const subfilterRef = useRef(null);
  const [subfilterScroll, setSubfilterScroll] = useState({ canLeft: false, canRight: false });

  const updateSubfilterScroll = useCallback(() => {
    const el = subfilterRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setSubfilterScroll({
      canLeft: el.scrollLeft > 4,
      canRight: maxScroll > 4 && el.scrollLeft < maxScroll - 4,
    });
  }, []);

  useEffect(() => {
    updateSubfilterScroll();
    window.addEventListener('resize', updateSubfilterScroll);
    return () => window.removeEventListener('resize', updateSubfilterScroll);
  }, [visibleSubcats, selectedCategory, updateSubfilterScroll]);

  const scrollSubfilters = (direction) => {
    const el = subfilterRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(160, el.clientWidth * 0.6), behavior: 'smooth' });
  };

  const mainFilterPills = useMemo(() => {
    const pills = [{ id: 'todos', label: 'Todos los productos', count: sidebarBlocks.allItem.count }];
    sidebarBlocks.otherItems.filter((c) => c.count > 0).forEach((c) => {
      pills.push({ id: c.id, label: c.name, count: c.count });
    });
    if (sidebarBlocks.saleCount > 0) {
      pills.push({ id: 'sale', label: 'Ofertas', count: sidebarBlocks.saleCount });
    }
    return pills;
  }, [sidebarBlocks]);

  const selectCategory = (id) => {
    setSelectedCategory(id);
    setSelectedSubcategory(null);
  };

  const selectedCategoryLabel = selectedSubcategory
    ? (SUBCATEGORY_UI_LABELS[selectedSubcategory]?.label || selectedSubcategory)
    : (CATEGORY_UI_LABELS[selectedCategory] || selectedCategory);

  const searchFilteredProducts = useMemo(() => {
    const q = searchQuery.trim();
    if (!q) return filteredProducts;
    return filteredProducts.filter((p) => {
      const subLabel = SUBCATEGORY_UI_LABELS[p.subcategory]?.label || '';
      const haystack = [p.name, p.brand, p.description, p.category, p.subcategory, subLabel]
        .filter(Boolean)
        .join(' ');
      return searchTextIncludes(haystack, q);
    });
  }, [filteredProducts, searchQuery]);

  const groupedProducts = useMemo(
    () => groupProductsForStore(searchFilteredProducts),
    [searchFilteredProducts]
  );

  const sortedProducts = [...groupedProducts].sort((a, b) => {
    // Prioridad fija: 0=bodega Javiland (stock>0), 1=dropshipping disponible, 2=agotado real
    const priority = (p) => {
      if (p.stock > 0) return 0;
      if (p.isDropshipping) return 1;
      return 2;
    };
    const pa = priority(a);
    const pb = priority(b);
    if (pa !== pb) return pa - pb;

    // Dentro del mismo grupo, aplicar el criterio elegido por el usuario
    if (sortBy === 'price-asc') return a.price - b.price;
    if (sortBy === 'price-desc') return b.price - a.price;
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    return 0;
  });

  const formatStorePrice = (amount) =>
    `$${Math.round(amount).toLocaleString('es-CO')}`;

  const handleAddToCart = (product) => {
    try {
      const productForCart = {
        id: product.id,
        name: product.name,
        price: product.price,
        images: [product.image || '/images/placeholders/placeholder-product.png'],
        variants: [{ size: 'Único', color: 'Único', stock: product.stock }]
      };
      addToCart(productForCart, 'Único', 'Único', 1);
      openCart();
    } catch (err) {
      console.error('Error al agregar al carrito:', err);
      alert('Error al agregar el producto al carrito.');
    }
  };

  const goToProduct = (product) => {
    window.history.pushState({}, '', `/producto/${product.id}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="tienda-page">
      <Header />
      <AnimatedBackground particleCount={20} staticCount={6} />

      {/* Carrusel igual que Bienestar - imágenes IA de fondo */}
      <StoreCarousel slides={carouselSlides} autoplayInterval={6000} height="550px" variant="tienda" />

      <main className="tienda-main">
        <div className="tienda-shell">
          <header className="tienda-header">
            <div className="tienda-header-top">
              <div className="tienda-header-intro">
                <p className="tienda-kicker">Tienda Javiland</p>
                <h1 className="tienda-page-title">{selectedCategoryLabel}</h1>
              </div>
              <p className="tienda-shipping-note">
                <i className="fas fa-truck" aria-hidden="true" />
                Envíos a Colombia · Tarifa plana $12.000 COP
              </p>
            </div>

            <nav className="tienda-filter-bar" aria-label="Categorías de la tienda">
              {mainFilterPills.map((pill) => (
                <button
                  key={pill.id}
                  type="button"
                  className={`tienda-filter-pill ${selectedCategory === pill.id && !selectedSubcategory ? 'active' : ''}`}
                  onClick={() => selectCategory(pill.id)}
                >
                  {pill.label}
                  <span className="tienda-filter-pill-count">{pill.count}</span>
                </button>
              ))}
            </nav>

            {visibleSubcats.length > 0 && (
              <div className="tienda-subfilter-wrap">
                {subfilterScroll.canLeft && (
                  <button
                    type="button"
                    className="tienda-subfilter-scroll-btn tienda-subfilter-scroll-btn--left"
                    onClick={() => scrollSubfilters(-1)}
                    aria-label="Ver subcategorías anteriores"
                  >
                    <i className="fas fa-chevron-left" aria-hidden="true" />
                  </button>
                )}
                <nav
                  ref={subfilterRef}
                  className="tienda-subfilter-bar"
                  aria-label="Subcategorías"
                  onScroll={updateSubfilterScroll}
                >
                  <button
                    type="button"
                    className={`tienda-subfilter-pill ${!selectedSubcategory ? 'active' : ''}`}
                    onClick={() => setSelectedSubcategory(null)}
                  >
                    Todos
                  </button>
                  {visibleSubcats.map((sc) => (
                    <button
                      key={sc.id}
                      type="button"
                      className={`tienda-subfilter-pill ${selectedSubcategory === sc.id ? 'active' : ''}`}
                      onClick={() => setSelectedSubcategory(sc.id)}
                    >
                      {sc.name}
                    </button>
                  ))}
                </nav>
                {subfilterScroll.canRight && (
                  <>
                    <span className="tienda-subfilter-fade" aria-hidden="true" />
                    <button
                      type="button"
                      className="tienda-subfilter-scroll-btn tienda-subfilter-scroll-btn--right"
                      onClick={() => scrollSubfilters(1)}
                      aria-label="Ver más subcategorías"
                    >
                      <i className="fas fa-chevron-right" aria-hidden="true" />
                    </button>
                  </>
                )}
                {subfilterScroll.canRight && (
                  <span className="tienda-subfilter-hint">Desliza para ver más</span>
                )}
              </div>
            )}

            <div className="tienda-toolbar">
              <span className="product-count">
                {groupedProducts.length} {groupedProducts.length === 1 ? 'producto' : 'productos'}
                {searchQuery.trim() ? ` para "${searchQuery.trim()}"` : ''}
              </span>

              <div className="toolbar-actions">
                <div className="tienda-search">
                    <i className="fas fa-search tienda-search-icon" aria-hidden="true" />
                    <input
                      type="search"
                      className="tienda-search-input"
                      placeholder="Buscar producto…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      aria-label="Buscar productos en la tienda"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        className="tienda-search-clear"
                        onClick={() => setSearchQuery('')}
                        aria-label="Limpiar búsqueda"
                      >
                        <i className="fas fa-times" aria-hidden="true" />
                      </button>
                    )}
                  </div>

                  <div className="sort-dropdown">
                    <label htmlFor="sort-select">Ordenar por:</label>
                    <select
                      id="sort-select"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="sort-select"
                    >
                      <option value="latest">Más recientes</option>
                      <option value="price-asc">Precio: menor a mayor</option>
                      <option value="price-desc">Precio: mayor a menor</option>
                      <option value="name">Nombre A-Z</option>
                    </select>
                  </div>
                </div>
              </div>
            </header>

            {loading && (
              <div className="tienda-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Cargando productos...</p>
              </div>
            )}

            {error && (
              <div className="tienda-error">
                <i className="fas fa-exclamation-circle"></i>
                <p>{error}</p>
              </div>
            )}

            {!loading && !error && groupedProducts.length === 0 && (
              <div className="tienda-empty">
                <i className={`fas ${
                  searchQuery.trim()
                    ? 'fa-search'
                    : ['ropa', 'ropa-hombre', 'ropa-mujer'].includes(selectedCategory)
                    ? 'fa-tshirt'
                    : selectedCategory === 'combos'
                    ? 'fa-box-open'
                    : selectedCategory === 'productos-naturales'
                    ? 'fa-leaf'
                    : selectedCategory === 'sale'
                    ? 'fa-tags'
                    : 'fa-leaf'
                }`}></i>
                <p>
                  {searchQuery.trim()
                    ? `No encontramos productos para "${searchQuery.trim()}". Prueba con otro término.`
                    : selectedCategory === 'ropa' || selectedCategory === 'ropa-hombre' || selectedCategory === 'ropa-mujer'
                    ? 'Camisetas Javiland próximamente. Estamos preparando la colección.'
                    : selectedCategory === 'combos'
                    ? selectedSubcategory
                      ? 'No hay combos en esta subcategoría por ahora.'
                      : 'No hay combos disponibles por ahora.'
                    : selectedCategory === 'productos-naturales'
                    ? selectedSubcategory
                      ? 'No hay productos en esta subcategoría por ahora.'
                      : 'No hay productos naturales disponibles por ahora.'
                    : selectedCategory === 'todos' && selectedSubcategory
                    ? 'No hay productos en esta subcategoría por ahora.'
                    : selectedCategory === 'sale'
                    ? 'No hay productos con descuento activo por ahora.'
                    : 'No hay productos en esta categoría.'}
                </p>
              </div>
            )}

            {!loading && !error && groupedProducts.length > 0 && (
              <div className="tienda-products-grid">
                {sortedProducts.map((product) => {
                  const showPriceRange = product.hasMultiplePresentations && product.minPrice !== product.maxPrice;
                  const needsSelection = product.isCamisetaAgrupada || product.requiresPresentationSelect;

                  return (
                  <article
                    key={product.isGrouped ? `group-${product.baseName}-${product.category}` : product.id}
                    className="product-card"
                    onClick={(e) => {
                      if (e.target.closest('.product-quick-actions')) return;
                      goToProduct(product);
                    }}
                  >
                    <div className="product-image-wrap">
                      <img
                        src={product.image || product.images?.[0] || '/images/placeholders/placeholder.svg'}
                        alt={product.name}
                        referrerPolicy="no-referrer"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/placeholders/placeholder.svg';
                        }}
                      />
                      {product.isNew && <span className="product-badge new">Nuevo</span>}
                      {!product.inStock && <span className="product-badge soldout">Agotado</span>}
                      {product.hasMultiplePresentations && product.inStock && (
                        <span className="product-badge variants">Varias presentaciones</span>
                      )}
                      {!showPriceRange && product.originalPrice > product.price && (
                        <span className="product-badge discount">
                          -{Math.round((1 - product.price / product.originalPrice) * 100)}%
                        </span>
                      )}

                      <div className="product-quick-actions">
                        <button
                          className="quick-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (needsSelection) goToProduct(product);
                            else if (product.inStock) handleAddToCart(product);
                          }}
                          disabled={!product.inStock && !needsSelection}
                          title={needsSelection ? 'Elegir presentación' : 'Agregar al carrito'}
                        >
                          <i className={needsSelection ? 'fas fa-list-ul' : 'fas fa-shopping-bag'}></i>
                        </button>
                        <button
                          className="quick-action-btn"
                          onClick={(e) => { e.stopPropagation(); goToProduct(product); }}
                          title="Ver producto"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </div>
                    </div>

                    <div className="product-info">
                      {product.brand && (
                        <span className="product-brand">{product.brand}</span>
                      )}
                      <h3 className="product-name" title={product.name}>{product.name}</h3>
                      {(normalizeCategorySlug(product.category) === 'combos' || isMixDisplayProduct(product)) && product.description && (
                        <p className="product-combo-tagline">{productKitTagline(product.description)}</p>
                      )}
                      <div className="product-pricing">
                        {showPriceRange ? (
                          <>
                            <span className="tienda-card-price">
                              {formatStorePrice(product.minPrice)} – {formatStorePrice(product.maxPrice)}
                            </span>
                            <span className="tienda-price-currency">COP</span>
                          </>
                        ) : (
                          <>
                            <span className="tienda-card-price">{formatStorePrice(product.price)}</span>
                            <span className="tienda-price-currency">COP</span>
                            {product.originalPrice > product.price && (
                              <>
                                <span className="product-discount-badge">
                                  -{Math.round((1 - product.price / product.originalPrice) * 100)}%
                                </span>
                                <span className="tienda-price-original">{formatStorePrice(product.originalPrice)}</span>
                              </>
                            )}
                          </>
                        )}
                      </div>
                      <button
                        className="product-cta"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (needsSelection) goToProduct(product);
                          else handleAddToCart(product);
                        }}
                        disabled={!product.inStock && !needsSelection}
                      >
                        {needsSelection
                          ? 'Elegir presentación'
                          : (product.inStock ? 'Agregar' : 'Agotado')}
                      </button>
                    </div>
                  </article>
                  );
                })}
              </div>
            )}
        </div>
      </main>

      <TiendaServicesCrossLink />
    </div>
  );
};

export default TiendaPage;
